import { Suspense } from 'react'
import { Routes, Route, Navigate, useParams, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import { Layout } from '@/components/layout'
import { ScrollToTop } from '@/components/ScrollToTop'
import { CookieBanner } from '@/components/CookieBanner'
import { BackToTop } from '@/components/BackToTop'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { ChatBotProvider } from '@/contexts/ChatBotContext'
import {
  Home,
  AboutUs,
  Gestione,
  Consulting,
  Training,
  WorkWithUs,
  WhereWeAre,
  Contacts,
  LegalNotes,
  Privacy,
  CookiePolicy,
  NotFound,
} from '@/pages'
import { supportedLanguages, defaultLanguage, type SupportedLanguage } from '@/i18n'

function LanguageWrapper() {
  const { lang } = useParams<{ lang: string }>()
  const { i18n } = useTranslation()

  useEffect(() => {
    if (lang && supportedLanguages.includes(lang as SupportedLanguage)) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang)
      }
    }
  }, [lang, i18n])

  // Redirect invalid language to default
  if (!lang || !supportedLanguages.includes(lang as SupportedLanguage)) {
    return <Navigate to={`/${defaultLanguage}`} replace />
  }

  return (
    <Layout>
      <Outlet />
    </Layout>
  )
}

function App() {
  return (
    <ChatBotProvider>
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
        <ScrollToTop />
        <Routes>
          {/* Redirect root to default language */}
          <Route path="/" element={<Navigate to={`/${defaultLanguage}`} replace />} />

          {/* Language-prefixed routes */}
          <Route path="/:lang" element={<LanguageWrapper />}>
            <Route index element={<Home />} />

            {/* Italian routes */}
            <Route path="chi-siamo" element={<AboutUs />} />
            <Route path="gestione" element={<Gestione />} />
            <Route path="consulenza" element={<Consulting />} />
            <Route path="formazione" element={<Training />} />
            <Route path="lavora-con-noi" element={<WorkWithUs />} />
            <Route path="dove-siamo" element={<WhereWeAre />} />
            <Route path="contatti" element={<Contacts />} />
            <Route path="note-legali" element={<LegalNotes />} />

            {/* English routes */}
            <Route path="about-us" element={<AboutUs />} />
            <Route path="management" element={<Gestione />} />
            <Route path="consulting" element={<Consulting />} />
            <Route path="training" element={<Training />} />
            <Route path="work-with-us" element={<WorkWithUs />} />
            <Route path="where-we-are" element={<WhereWeAre />} />
            <Route path="contacts" element={<Contacts />} />
            <Route path="legal-notes" element={<LegalNotes />} />

            {/* Common routes (same in both languages) */}
            <Route path="privacy" element={<Privacy />} />
            <Route path="cookie-policy" element={<CookiePolicy />} />

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
        <BackToTop />
        <CookieBanner />
      </Suspense>
    </ChatBotProvider>
  )
}

export default App
